<template>
    <div
        v-if="!$wait.is('loading-page')"
        class="m-referral-program"
    >
        <div class="m-referral-program__section-one">
            <y-container>
                <y-pwa-video
                    iframe
                    iframe-field="field9"
                    :streaming-data="heroVideoData"
                    dont-show-content
                    title-tag="h1"
                    button-class="scroll-to-referral-form"
                />
            </y-container>
        </div>

        <div class="m-referral-program__section-two">
            <y-container>
                <y-pwa-types
                    :pwa-types="whyJoinUs"
                    :custom-btn="whyJoinUs.custom"
                    button-class="scroll-to-referral-form"
                />
            </y-container>
        </div>

        <div class="m-referral-program__section-three">
            <y-container>
                <y-how-referral-works
                    :content="howItWorks"
                    button-class="scroll-to-referral-form"
                />
            </y-container>
        </div>

        <div class="m-referral-program__section-four">
            <y-container>
                <y-faq
                    filter-slug="referral-program"
                    title-tag="h3"
                    actual-title-tag="h2"
                    :actual-title-text="faqTitle"
                />
            </y-container>
        </div>

        <div class="m-referral-program__section-five">
            <y-container>
                <y-cta-complex
                    v-if="ctaFirst"
                    :title="ctaFirst.title"
                    :description="ctaFirst.synopsis"
                    :solid-btn-title="ctaFirst.buttonTitle"
                    :solid-btn-link="ctaFirst.buttonLink"
                    :image-src="ctaFirst.image"
                    title-tag="p"
                    solid-button-class="scroll-to-referral-form"
                />
            </y-container>
        </div>

        <div class="m-referral-program__section-six">
            <y-container>
                <y-our-client-say
                    :content="testimonials.content"
                    :clients-say="testimonials.list"
                    title-tag="h2"
                />
            </y-container>
        </div>

        <div class="m-referral-program__section-seven">
            <y-container>
                <y-cta-complex
                    v-if="ctaSecond.blue"
                    class="blue"
                    :title="ctaSecond.blue.title"
                    :description="ctaSecond.blue.subtitle"
                    :solid-btn-title="ctaSecond.blue.buttonTitle"
                    :solid-btn-link="ctaSecond.blue.buttonLink"
                    :image-src="ctaSecond.blue.image"
                    title-tag="h2"
                />
                <y-cta-complex
                    v-if="ctaSecond.pink"
                    class="pink"
                    :title="ctaSecond.pink.title"
                    :description="ctaSecond.pink.subtitle"
                    :solid-btn-title="ctaSecond.pink.buttonTitle"
                    :solid-btn-link="ctaSecond.pink.buttonLink"
                    :image-src="ctaSecond.pink.image"
                    title-tag="h2"
                />
            </y-container>
        </div>
        <div class="m-referral-program__section-eight">
            <y-container>
                <y-form-section
                    :form-config="formSection.config"
                    :content="formSection.titleAndImage"
                    :countries="formSection.countries"
                />
            </y-container>
        </div>
    </div>
</template>

<script>
    // Mixins
    import PageMixin from '@groot-nodes/mixins/Page.js';

    import YPwaVideo from '@modules/pwa/components/PwaVideo/Index.vue';
    import YOurClientSay from '@/components/OurClientsSay/Index.vue';
    import YFaq from '@modules/referral-program/components/faq/Faq.vue';
    import YPwaTypes from '@modules/pwa/components/PwaTypes/index.vue';
    import YCtaComplex from '@components/ForPagesComponents/CtaComplex/CtaComplex.vue';
    import YHowReferralWorks from '@modules/referral-program/components/HowDoesReferralWork/Index.vue';
    import YFormSection from '@modules/referral-program/components/FormSection/Index.vue';

    export default {
        name: 'ReferralProgramPage',

        components: {
            YPwaVideo,
            YOurClientSay,
            YFaq,
            YPwaTypes,
            YCtaComplex,
            YHowReferralWorks,
            YFormSection,
        },

        mixins: [PageMixin],

        /**
         * @inheritdoc
         */
        data() {
            return {
                heroVideoData: {},
                whyJoinUs    : {},
                howItWorks   : {},
                faqTitle     : {},
                ctaFirst     : {},
                testimonials : {},
                ctaSecond    : {},
                formSection  : {},
            };
        },

        /**
         * @inheritDoc
         */
        metaInfo() {
            return {
                title: this.heroVideoData.seo_post_title,

                meta: [
                    {
                        vmid   : 'description',
                        name   : 'description',
                        content: this.heroVideoData.seo_meta_description,
                    },
                ],
            };
        },

        /**
         *
         * @inheritDoc
         */
        methods: {
            /**
             * Fetches Referral program page data
             *
             * @returns {Promise} Promise object assigns the components data when resolved
             */
            fetch() {
                // Fetches the hero, video and why us sections data (1-3)
                const heroVideoAndWhyUs = this.$services.Content.postSingle({
                    type     : 'landing-page',
                    slug     : 'referral-program',
                    including: ['title', 'synopsis', 'image', 'header_image', 'custom', 'slug', 'albums', 'seo_canonical', 'seo_meta_description', 'seo_post_title'],
                    locale   : this.$i18n.locale,
                }).catch((error) => {
                    this.handleError(error);
                });

                // Fetches the how it works, FAQ title and the first CTA (the one with image) sections data (3-5)
                const howItWorksFAQTitleAndFirstCTA = this.$services.Content.postSingle({
                    type     : 'landing-page',
                    slug     : 'referral-program-2',
                    including: ['title', 'title2', 'synopsis', 'image', 'custom', 'albums'],
                    locale   : this.$i18n.locale,
                }).catch((error) => {
                    this.handleError(error);
                });

                // Fetches the testimonials title, image and the last two CTAs sections data (6-7)
                const testimonialsTitleAndLastCTAs = this.$services.Content.postSingle({
                    type     : 'landing-page',
                    slug     : 'referral-program-3',
                    including: ['title', 'image', 'custom'],
                    locale   : this.$i18n.locale,
                }).catch((error) => {
                    this.handleError(error);
                });

                // (6)
                const testimonials = this.$services.Content.postList({
                    type     : 'testimonials',
                    tagging  : 'referral-program',
                    including: ['title', 'title2', 'header_image', 'image', 'synopsis'],
                    locale   : this.$i18n.locale,
                }).catch((error) => {
                    this.handleError(error);
                });

                // Form section (8)
                const formSection = this.$services.Content.postSingle({
                    type     : 'pages',
                    slug     : 'referral-program',
                    including: ['title', 'image'],
                    locale   : this.$i18n.locale,
                }).catch((error) => {
                    this.handleError(error);
                });

                // Form config (8)
                const formConfigs = this.$services.Commenting.getFormConfig({
                    type  : 'pages',
                    slug  : 'referral-program',
                    locale: this.$i18n.locale,
                }).catch((error) => {
                    this.handleError(error);
                });

                // Form section (8)
                const countriesList = this.$services.Endpoint.modelList({
                    model    : 'division',
                    types    : ['country'],
                    including: ['id', 'title'],
                    locale   : this.$i18n.locale,
                    sort     : `title_${this.$i18n.locale}`,
                    order    : 'asc',
                }).catch((error) => {
                    this.handleError(error);
                });

                return Promise.all(
                    [heroVideoAndWhyUs, howItWorksFAQTitleAndFirstCTA, testimonialsTitleAndLastCTAs, testimonials, formSection, formConfigs, countriesList],
                ).then((responses) => {
                    // heroVideoAndWhyUs
                    if (this.handleReq(responses, 0)) {
                        this.heroVideoData = {
                            title               : responses[0].data.results.title,
                            synopsis            : responses[0].data.results.synopsis,
                            header_image        : responses[0].data.results.header_image,
                            albums              : responses[0].data.results.albums.filter((item) => item.group === 'video'),
                            image               : responses[0].data.results.image,
                            seo_canonical       : responses[0].data.results.seo_canonical,
                            seo_meta_description: responses[0].data.results.seo_meta_description,
                            seo_post_title      : responses[0].data.results.seo_post_title,
                            custom              : {
                                embed_link    : responses[0].data.results?.custom?.field9,
                                'button-link' : '#referral-form',
                                'button-title': responses[0].data.results.custom['button-title'],
                            },
                        };

                        this.whyJoinUs = {
                            title : responses[0].data.results.custom.field1,
                            albums: responses[0].data.results.albums.filter((item) => item.group === 'album'),
                            custom: {
                                'button-link' : '#referral-form',
                                'button-title': responses[0].data.results.custom.field2,
                            },
                        };
                    }
                    // howItWorksFAQTitleAndFirstCTA
                    if (this.handleReq(responses, 1)) {
                        this.howItWorks = {
                            title : responses[1].data.results.title,
                            note  : responses[1].data.results.synopsis,
                            album : responses[1].data.results.albums.filter((item) => item.group === 'album'),
                            custom: {
                                'button-link' : '#referral-form',
                                'button-title': responses[1].data.results.custom['button-title'],
                            },
                        };

                        this.faqTitle = responses[1].data.results.title2;

                        this.ctaFirst = {
                            title      : responses[1].data.results.custom.field1,
                            synopsis   : responses[1].data.results.custom.field2,
                            image      : responses[1].data.results.image,
                            buttonTitle: responses[1].data.results.custom.field3,
                            buttonLink : '#referral-form',
                        };
                    }
                    // testimonialsTitleAndLastCTAs
                    if (this.handleReq(responses, 2)) {
                        this.testimonials = {
                            ...this.testimonials,
                            content: {
                                title: responses[2].data.results.title,
                                image: responses[2].data.results.image,
                            },
                        };

                        this.ctaSecond = {
                            blue: {
                                title      : responses[2].data.results.custom.field1,
                                subtitle   : responses[2].data.results.custom.field2,
                                buttonTitle: responses[2].data.results.custom.field3,
                                buttonLink : responses[2].data.results.custom.field4,
                            },
                            pink: {
                                title      : responses[2].data.results.custom.field5,
                                subtitle   : responses[2].data.results.custom.field6,
                                buttonTitle: responses[2].data.results.custom.field7,
                                buttonLink : responses[2].data.results.custom.field8,
                            },
                        };
                    }
                    // testimonials
                    if (this.handleReq(responses, 3)) {
                        this.testimonials = {
                            ...this.testimonials,
                            list: responses[3].data.results,
                        };
                    }
                    // Form data
                    if (this.handleReq(responses, 4)) {
                        this.formSection = {
                            ...this.formSection,
                            titleAndImage: responses[4].data.results,
                        };
                    }
                    // Form config
                    if (this.handleReq(responses, 5)) {
                        this.formSection = {
                            ...this.formSection,
                            config: {
                                ...responses[5].data.results,
                                recaptcha: responses[5].data.metadata.recaptcha,
                            },
                        };
                    }
                    // Form config country list
                    if (this.handleReq(responses, 6)) {
                        this.formSection = {
                            ...this.formSection,
                            countries: responses[6].data.results.map((item) => ({ value: item.id, label: item.title })),
                        };
                    }
                }).catch((error) => {
                    this.handleError(error);
                }).finally(() => {
                    if (window.location.hash === '#submit-form') {
                        setTimeout(() => {
                            document.getElementById('c-referral-form').scrollIntoView({
                                behavior: 'smooth',
                            });
                        }, 300);
                    }
                });
            },
        },
    };
</script>

<style lang="scss" src="../scss/referral-program.scss"></style>
